import { Tooltip } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  SearchBox,
  SingleSearchSelector,
  Option,
  Table,
  TableIconButton,
  TbodyInfiniteScroll,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  useTranslation,
  IconButton,
} from "@familyzone/component-library";
import { toInteger } from "lodash";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  StudentActionEvent,
  StudentActionEventPlaceholder,
  StudentActionType,
  Guardian,
  GuardianPlaceholder,
  isLocalGuardian,
  PurgeScope,
  Student,
} from "../../types/Community";
import { zIndices } from "../../utils/ZIndexUtil";
import ParentDetailsModal from "../modals/ParentDetailsModal";
import StudentDisconnectModal from "../modals/StudentDisconnectModal";
import PurgeParentsModal from "../modals/PurgeParentsModal";
import CardBasedPage from "../templates/CardBasedPage";
import EditParentModal from "../modals/EditParentModal";
import { FilterGuardianOptions, useGuardianStore } from "../../storez/GuardianStore";
import { CommunityConfigStore, useCommunityConfigStore } from "../../storez/CommunityConfigStore";
import ExportParentsModal from "../modals/ExportParentsModal";
import { ResponseError } from "../../types/Api";
import { useSessionStore } from "../../storez/SessionStore";
import { DeleteParentModal } from "../modals/DeleteParentModal";
import { GroupOption } from "../../types/Groups";
import { GroupSearchSelector } from "../GroupSearch/GroupSearchSelector";
import { hasFilter } from "../../storez/GuardianStore";

/* An Option where the value is always a string. */
interface StringOption extends Option {
  value: string;
}

const ParentManagement: React.FC = () => {
  const rowRef = useRef<HTMLTableRowElement>(null);

  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const showSuccessToast = (options: { title: string; description: string }): void => {
    successToast({ ...options, isClosable: true });
  };

  const showErrorToast = (options: { title: string; description: string }): void => {
    errorToast({ ...options, isClosable: true });
  };

  const showFetchErrorToast = (): void => {
    showErrorToast({ title: t("Please try again"), description: t("Failed to load guardians") });
  };

  const title = t("Parent Management");
  const breadcrumbs = [
    { title: t("Parent"), url: "/community", isActive: false },
    { title: t("Parent Management"), isActive: true },
  ];

  const studentStatusOptions: StringOption[] = [
    { label: "Restricted Students", value: "RESTRICTED" },
    { label: "Connected Students", value: "CONNECTED" },
    { label: "Assigned Students", value: "ASSIGNED" },
  ];

  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const pageSize = useRef<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [exported, setExported] = useState<boolean>(false);
  const [isActiveSearch, setIsActiveSearch] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedStudentStatusOption, setSelectedStudentStatusOption] = useState<StringOption | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [filterTimeout, setFilterTimeout] = useState<ReturnType<typeof setTimeout>>();

  const [selectedState, setSelectedState] = useState<{ guardian: Guardian; students?: Student[] }>();
  const [editState, setEditState] = useState<{ guardian: Guardian }>();
  const [studentActionState, setStudentActionState] = useState<StudentActionEvent>();
  const [guardianToDelete, setGuardianToDelete] = useState<Guardian>();
  const [showPurgeParentsModal, setShowPurgeParentsModal] = useState<boolean>(false);
  const [showExportParentsModal, setShowExportParentsModal] = useState<boolean>(false);

  const guardianFilter = useMemo(() => {
    return {
      term: searchTerm,
      studentStatus: selectedStudentStatusOption?.value ?? "",
      groupId: selectedGroup?.id ?? "",
    };
  }, [searchTerm, selectedStudentStatusOption, selectedGroup]);

  const isFiltering = useMemo(() => hasFilter(guardianFilter), [guardianFilter]);

  const [guardians, hasNextPage, fetchGuardians, downloadAllGuardians, setGuardian, resetGuardianStore] = useGuardianStore(
    useCallback(
      (state) =>
        [state.guardians, state.hasNextPage, state.fetchGuardians, state.downloadAllGuardians, state.setGuardian, state.reset] as const,
      []
    )
  );

  const [storedCommunityConfig, fetchCommunityConfig] = useCommunityConfigStore(
    useCallback((state: CommunityConfigStore) => [state.config, state.fetch] as const, [])
  );

  useEffect(() => {
    void fetchCommunityConfig();
  }, [fetchCommunityConfig]);

  useEffect(() => {
    const pageHeight = window.innerHeight;
    const rowHeight = Math.max(rowRef?.current?.offsetHeight ?? 40, 40);
    pageSize.current = toInteger(pageHeight / rowHeight);

    void fetchGuardians({ term: "", studentStatus: "", cursor: "", details: true, pageSize: pageSize.current }).then(() => {
      setLoading(false);
    }, showFetchErrorToast);

    return () => resetGuardianStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchGuardians]);

  const executeFetch = (options: { term: string; studentStatus?: string; groupId?: string; cursor: string }): void => {
    if (filterTimeout) {
      clearTimeout(filterTimeout);
    }

    setFilterTimeout(
      setTimeout(() => {
        setLoading(true);
        fetchGuardians({ ...options, details: true, pageSize: pageSize.current }).then(() => {
          setLoading(false);
        }, showFetchErrorToast);
      }, 1000)
    );
  };

  const executeDownload = (filter: FilterGuardianOptions): void => {
    setLoading(true);
    setExported(false);

    void downloadAllGuardians(filter).then(
      () => {
        setLoading(false);
        setExported(true);
      },
      (err: ResponseError) => {
        showErrorToast({ title: t("Please try again"), description: t(err.message) });
        setLoading(false);
        setExported(false);
      }
    );
  };

  const onGroupSelection = (selected: GroupOption | null): void => {
    let groupId = "";
    if (!selected) {
      setSelectedGroup(null);
    } else {
      setSelectedGroup(selected);
      groupId = selected.id ? selected.id.toString() : "";
    }

    if (selected?.id !== selectedGroup?.id) {
      // only execute the search if the chosen group is different than the existing one
      setPageNumber(0);
      executeFetch({ term: searchTerm, studentStatus: selectedStudentStatusOption?.value, groupId: groupId, cursor: "" });
    }
  };

  const onChangedStudentStatusOption = (selected: StringOption | null) => {
    let statusValue = "";
    if (!selected) {
      setSelectedStudentStatusOption(null);
    } else {
      setSelectedStudentStatusOption(selected);
      statusValue = selected.value ? selected.value : "";
    }
    if (selected?.value !== selectedStudentStatusOption?.value) {
      setPageNumber(0);
      executeFetch({ term: searchTerm, studentStatus: statusValue, groupId: selectedGroup?.id, cursor: "" });
    }
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setSearchTerm(e.target.value);
    if (value && value.length > 2) {
      setPageNumber(0);
      setIsActiveSearch(true);
      executeFetch({ term: e.target.value, studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });
    } else if (isActiveSearch && !value) {
      // clear the results from the previous search
      setPageNumber(0);
      setIsActiveSearch(false);
      executeFetch({ term: "", studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });
    }
  };

  const onSearchClear = (): void => {
    setSearchTerm("");
    setIsActiveSearch(false);
    executeFetch({ term: "", studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });
  };

  const handleClearAllFilters = (): void => {
    setSearchTerm("");
    setIsActiveSearch(false);
    setSelectedStudentStatusOption(null);
    setSelectedGroup(null);
    setPageNumber(0);
    executeFetch({ term: "", studentStatus: "", groupId: "", cursor: "" });
  };

  const handleLoadMore = (): void => {
    if (loading || !hasNextPage) {
      return;
    }

    setLoading(true);
    const cursor = guardians[guardians.length - 1]?.cursor ?? "";
    setPageNumber(pageNumber + 1);
    executeFetch({
      term: searchTerm,
      studentStatus: selectedStudentStatusOption?.value,
      groupId: selectedGroup?.id,
      cursor: cursor,
    });
  };

  const fetchStudents = (guardian: Guardian): void => {
    void useGuardianStore
      .getState()
      .fetchGuardianDetails(guardian.email)
      .then(
        (g) => {
          // refresh 'Parent Details' modal with students
          setSelectedState({ guardian: g, students: g.students });
        },
        () => {
          showErrorToast({
            title: t("Please try again"),
            description: t("An unexpected error occurred while loading parent details."),
          });
        }
      );
  };

  const restrictClaimedStudent = (guardian: Guardian, student: Student): void => {
    void useGuardianStore
      .getState()
      .removeClaim(student, guardian)
      .then(
        (guardian) => {
          // propagate guardian changes
          setGuardian(guardian);

          // propagate student changes
          setSelectedState({ guardian, students: guardian.students });

          // close confirmation modal
          setStudentActionState(undefined);

          // update parent list
          setPageNumber(0);
          executeFetch({ term: searchTerm, studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });

          showSuccessToast({
            title: t("Restriction successful"),
            description: t("Student") + ` ${student.firstName} ${student.lastName} ` + t("has been successfully restricted"),
          });
        },
        (_: ResponseError) => {
          setStudentActionState(undefined);
          showErrorToast({
            title: t("Please try again"),
            description: t("There was an error restricting ") + ` ${student.firstName} ${student.lastName} `,
          });
        }
      );
  };

  const restrictLinkedStudent = (guardian: Guardian, student: Student): void => {
    void useGuardianStore
      .getState()
      .restrictStudent(student, guardian)
      .then(
        (guardian) => {
          // propagates guardian changes
          setGuardian(guardian);

          // propagate student changes
          setSelectedState({ guardian, students: guardian.students });

          // close confirmation modal
          setStudentActionState(undefined);

          // update parent list
          setPageNumber(0);
          executeFetch({ term: searchTerm, studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });

          showSuccessToast({
            title: t("Restriction successful"),
            description: t("Student") + ` ${student.firstName} ${student.lastName} ` + t("has been successfully restricted"),
          });
        },
        (_: ResponseError) => {
          setStudentActionState(undefined);
          showErrorToast({
            title: t("Please try again"),
            description: t("There was an error restricting ") + ` ${student.firstName} ${student.lastName} `,
          });
        }
      );
  };

  const unRestrictStudent = (guardian: Guardian, student: Student): void => {
    void useGuardianStore
      .getState()
      .unRestrictStudent(student, guardian)
      .then(
        (guardian) => {
          // propagates guardian changes
          setGuardian(guardian);

          // propagate student changes
          setSelectedState({ guardian, students: guardian.students });

          // close confirmation modal
          setStudentActionState(undefined);

          // update parent list
          setPageNumber(0);
          executeFetch({ term: searchTerm, studentStatus: selectedStudentStatusOption?.value, groupId: selectedGroup?.id, cursor: "" });

          showSuccessToast({
            title: t("Unrestricted successfully"),
            description:
              ` ${guardian.firstName} ${guardian.middleName ?? ""} ${guardian.lastName} ` +
              t("can now connect to") +
              ` ${student.firstName} ${student.lastName} `,
          });
        },
        (_: ResponseError) => {
          setStudentActionState(undefined);
          showErrorToast({
            title: t("Please try again"),
            description: t("There was an error unrestricting ") + ` ${student.firstName} ${student.lastName} `,
          });
        }
      );
  };

  const handleOpenParentDetailsModal = (guardian: Guardian): void => {
    // open modal in 'loading' state with spinner visible
    setSelectedState({ guardian });

    // fetch students (on success updates modal to 'ready' state)
    fetchStudents(guardian);
  };

  const handleStudentAction = (event: StudentActionEvent): void => {
    // open confirmation modal
    setStudentActionState(event);
  };

  const handleCloseParentDetailsModal = (): void => {
    setStudentActionState(undefined);
    setSelectedState(undefined);
  };

  const handleCloseStudentDisconnectModal = (): void => {
    // close confirmation modal
    setStudentActionState(undefined);
  };

  const handleConfirmStudentRestrict = (): void => {
    if (studentActionState?.type === StudentActionType.DisconnectStudentFromParent) {
      restrictClaimedStudent(studentActionState.guardian, studentActionState.student);
    } else if (studentActionState?.type === StudentActionType.UnlinkStudentFromParent) {
      restrictLinkedStudent(studentActionState.guardian, studentActionState.student);
    } else if (studentActionState?.type === StudentActionType.UnRestrictStudent) {
      unRestrictStudent(studentActionState.guardian, studentActionState.student);
    }
  };

  const handleOpenParentEditModal = (guardian: Guardian): void => {
    // open 'Edit Parent' modal
    setEditState({ guardian });
  };

  const handleUpdateParentSuccess = (guardian: Guardian): void => {
    // propagate guardian changes
    setGuardian(guardian);

    // refresh 'Parent Details' modal
    setSelectedState((selected) => ({ ...selected, guardian }));

    // close 'Edit Parent' modal
    setEditState(undefined);
  };

  const handleCloseParentEditModal = (): void => {
    setEditState(undefined);
  };

  const handleOpenPurgeParentsModal = (): void => {
    setShowPurgeParentsModal(true);
  };

  const handleClosePurgeParentsModal = (): void => {
    setShowPurgeParentsModal(false);
  };

  const handleOpenExportParentsModal = (): void => {
    setExported(false);
    setShowExportParentsModal(true);
  };

  const handleCloseExportParentsModal = (): void => {
    setShowExportParentsModal(false);
  };

  const purgeParents = (purgeSet: PurgeScope): void => {
    void useGuardianStore
      .getState()
      .purgeGuardians({ scope: purgeSet })
      .then(
        () => {
          handleClearAllFilters();
          handleClosePurgeParentsModal();
          showSuccessToast({
            title: t("Purge Initiated Successfully"),
            description: "Purge parents operation initiated successfully. It should be completed soon.",
          });
        },
        (err: ResponseError) => {
          showErrorToast({
            title: t("Please try again"),
            description: t(err.message),
          });
        }
      );
  };
  const handleConfirmDeleteGuardian = () => {
    if (!guardianToDelete) throw new Error("Unexpected state");

    void useGuardianStore
      .getState()
      .deleteGuardian(guardianToDelete)
      .then(
        () => {
          // close confirmation modal
          setGuardianToDelete(undefined);

          showSuccessToast({
            title: t("Parent deleted successfully"),
            description: t("Parent") + ` ${guardianToDelete.firstName} ${guardianToDelete.lastName} ` + t("has been successfully deleted"),
          });
        },
        (err: ResponseError) => {
          showErrorToast({
            title: t("Please try again"),
            description: t(err.message),
          });
        }
      );
  };

  const handleCancelDeleteGuardian = () => {
    setGuardianToDelete(undefined);
  };

  return (
    <>
      <CardBasedPage
        title={title}
        breadcrumbs={breadcrumbs}
        actions={
          <Flex gap="sp24" justifyContent="end">
            {isSupportAdmin && (
              <Button variant="danger" disabled={loading || guardians.length === 0} onClick={handleOpenPurgeParentsModal}>
                {t("Purge Parents")}
              </Button>
            )}
          </Flex>
        }
      >
        <Box minHeight="200px" p="sp24">
          <Flex justifyContent="space-between" mb="sp16">
            <Flex gap="sp8">
              <Box>
                <SearchBox
                  value={searchTerm}
                  onChange={onSearchChange}
                  onClear={onSearchClear}
                  isDisabled={loading}
                  width="230px"
                  placeholder={t("Search for a parent by name or email")}
                  aria-label={t("Parent Search")}
                />
                <Text color="neutrals.200" mt="sp4" fontSize="sm">
                  {t("Please enter 3 or more characters.")}
                </Text>
              </Box>
              <GroupSearchSelector disabled={loading} placeholderText={t("Filter by Group")} handleChange={onGroupSelection} />
              <Box>
                <SingleSearchSelector
                  options={studentStatusOptions}
                  selected={selectedStudentStatusOption}
                  onChange={onChangedStudentStatusOption}
                  filterOptions={false}
                  showClearIcon={true}
                  placeholder={t("Filter by student status")}
                  disabled={loading}
                  name="student-status-selector"
                  aria-label={t("Student Status Selector")}
                />
                <Text color="neutrals.200" mt="sp4" fontSize="sm">
                  {t("")}
                </Text>
              </Box>
            </Flex>
            <Flex gap="sp24" justifyContent="end">
              <Box>
                <Button variant="primary" onClick={handleOpenExportParentsModal} disabled={loading}>
                  {t(isFiltering ? "Export Filtered Parent Data" : "Export Parent Data")}
                </Button>
              </Box>
            </Flex>
          </Flex>
          <Table>
            <Thead position="sticky" top="0" zIndex={zIndices.thead}>
              <Tr ref={rowRef}>
                <Th columnName="parent.name" headerText={t("Parent Name")} />
                <Th columnName="parent.email" headerText={t("Email")} />
                <Th columnName="parent.total_students" headerText={t("Total Students")} />
                <Th
                  columnName="parent.claimed_students"
                  headerText={t("Connected Students")}
                  iconPosition="right"
                  icon={<TooltipIcon text={t("Students listed below are connected to this parent through the Qustodio App.")} />}
                />
                <Th
                  columnName="parent.linked_students"
                  headerText={t("Assigned Students")}
                  iconPosition="right"
                  icon={
                    <TooltipIcon
                      text={t(
                        "Students listed below have been assigned to this parent, but are yet to be connected by the parent through the Qustodio App."
                      )}
                    />
                  }
                />
                <>
                  {storedCommunityConfig?.policyDelegation?.allowPolicyDelegation && (
                    <Th
                      columnName="parent.delegated_students"
                      headerText={t("Extended Rules Accepted")}
                      iconPosition="right"
                      icon={
                        <TooltipIcon
                          text={t(
                            "Students listed below have Extended Rules applied to their devices by their parents when out of school."
                          )}
                        />
                      }
                    />
                  )}
                </>
                <Th
                  columnName="parent.restricted_students"
                  headerText={t("Restricted Students")}
                  iconPosition="right"
                  icon={
                    <TooltipIcon
                      text={t(
                        "Students listed below have been restricted and can no longer be connected by the parent in the Qustodio App."
                      )}
                    />
                  }
                />
                <Th headerText="" />
              </Tr>
            </Thead>
            <TbodyInfiniteScroll
              parentElemId="ComponentWrapper"
              fetchData={() => handleLoadMore()}
              hasMore={hasNextPage || loading}
              loaded={guardians.length > 0 || !loading}
              searched={!!searchTerm}
            >
              {guardians.map((p, i) => (
                <Tr key={p.id} fontSize="md" color="text.title">
                  <Td>
                    <Text wordBreak="break-word">
                      {p.firstName} {p.lastName}
                    </Text>
                  </Td>
                  <Td>
                    <Text>
                      <Link className="clickable" onClick={() => handleOpenParentDetailsModal(p)}>
                        {p.email}
                      </Link>
                    </Text>
                  </Td>
                  <Td>
                    <Text>{String(p.totalCount ?? 0)}</Text>
                  </Td>
                  <Td>
                    <Text>{String(p.claimCount ?? 0)}</Text>
                  </Td>
                  <Td>
                    <Text>{String(p.linkCount ?? 0)}</Text>
                  </Td>
                  <>
                    {storedCommunityConfig?.policyDelegation?.allowPolicyDelegation && (
                      <Td>
                        <Text>{String(p.delegatedCount ?? 0)}</Text>
                      </Td>
                    )}
                  </>
                  <Td>
                    <Text>{String(p.restrictedCount ?? 0)}</Text>
                  </Td>
                  <Td>
                    <Flex gap="sp8">
                      <TableIconButton
                        icon={<Icon icon="fa-arrow-right" variant="solid" color="text.paragraph.light" />}
                        onClick={() => handleOpenParentDetailsModal(p)}
                        aria-label={t("View Details")}
                      />

                      <Tooltip
                        variant="dark"
                        placement={i === 0 ? "left" : "top"}
                        hasArrow={true}
                        maxWidth="304px"
                        isDisabled={isLocalGuardian(p) && !p.claimCount}
                        label={
                          !isLocalGuardian(p) ? "Synced parent cannot be deleted" : p.claimCount ? "Connected parent cannot be deleted" : ""
                        }
                      >
                        <Box>
                          <TableIconButton
                            icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
                            onClick={() => setGuardianToDelete(p)}
                            aria-label={t("Delete Parent")}
                            disabled={!isLocalGuardian(p) || !!p.claimCount}
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </TbodyInfiniteScroll>
          </Table>
        </Box>
      </CardBasedPage>
      <ParentDetailsModal
        open={!!selectedState}
        guardian={selectedState?.guardian ?? GuardianPlaceholder}
        students={selectedState?.students}
        policyDelegationEnabled={!!storedCommunityConfig?.policyDelegation?.allowPolicyDelegation}
        onClose={handleCloseParentDetailsModal}
        onStudentAction={handleStudentAction}
        onEdit={handleOpenParentEditModal}
      />
      <StudentDisconnectModal
        event={studentActionState ?? StudentActionEventPlaceholder}
        open={!!studentActionState}
        onClose={handleCloseStudentDisconnectModal}
        onConfirm={handleConfirmStudentRestrict}
      />
      <EditParentModal
        show={!!editState}
        guardian={editState?.guardian ?? GuardianPlaceholder}
        handleSuccess={handleUpdateParentSuccess}
        handleHide={handleCloseParentEditModal}
      />
      <PurgeParentsModal isOpen={showPurgeParentsModal} onClose={handleClosePurgeParentsModal} purgeParents={purgeParents} />
      <ExportParentsModal
        isOpen={showExportParentsModal}
        loading={loading}
        exported={exported}
        filter={guardianFilter}
        onClose={handleCloseExportParentsModal}
        executeDownload={executeDownload}
      />
      <DeleteParentModal
        open={!!guardianToDelete}
        guardian={guardianToDelete ?? GuardianPlaceholder}
        onConfirm={handleConfirmDeleteGuardian}
        onCancel={handleCancelDeleteGuardian}
      />
    </>
  );
};

function TooltipIcon({ text }: { text: string }) {
  return (
    <Tooltip label={text} variant="dark" placement="top" maxWidth="304px" zIndex={zIndices.tooltip} openDelay={300} gutter={12} hasArrow>
      {/* IconButton to make it keyboard accessible */}
      <IconButton
        aria-label={text}
        icon={<Icon icon="fa-circle-info" boxSize="12px" />}
        variant="ghost"
        color="neutrals.80"
        size="sm"
        cursor="default"
        boxSize="12px"
        transition="color 0.15s ease-in-out"
        display="flex"
        _hover={{
          color: "brand.500",
          backgroundColor: "transparent",
        }}
        _focusVisible={{
          color: "brand.500",
          backgroundColor: "transparent",
        }}
      />
    </Tooltip>
  );
}

export default ParentManagement;
